/**
 * Which addresses React answers. Everything else is still Python's.
 *
 * One list, read twice: `AppLink` uses it to decide between a router link and a plain
 * anchor, and `vite.config.ts` uses its inverse to decide what the dev server forwards to
 * the Python process instead of answering with the SPA shell.
 *
 * Those two have to agree or the migration breaks in a way that looks like nothing at all
 * happening: a plain anchor to `/calendar` is correct, and the dev server answered it with
 * index.html anyway, so React booted, matched no route and rendered an empty page.
 *
 * A route joins this list in the same commit that makes it real.
 *
 * Four of them did not. `/you`, `/search`, `/brief` and every `/u/<name>` were React routes
 * on the server's half of the list and missing from this one, so `AppLink` wrote a plain
 * anchor to each — a full page load, throwing away the app and rebuilding it, to reach a
 * route the app was already holding. Pressing your own name on the masthead was the loudest
 * of them. `test_client_contract` now reads both halves and refuses to let them disagree.
 */
export const PORTED = [
  '/',
  '/calendar',
  '/login',
  '/join',
  '/invite',
  '/export',
  '/tidy',
  '/you',
  '/profile',
  '/search',
  '/brief',
  '/studio',
  '/latency',
] as const

/** Ported too, but they carry a parameter, so membership is a prefix rather than equality.
 *  `/share-page/` is the card headless Chrome draws — the app's, outside the shell, and
 *  nothing on the site links to it. */
export const PORTED_PREFIXES = [
  '/e/',
  '/a/',
  '/v/',
  '/g/',
  '/u/',
  '/claim/',
  '/i/',
  '/share-page/',
] as const

/**
 * Page-shaped addresses the Python server still owns. Not the API — those are always its,
 * ported or not — but the documents a browser would navigate to.
 */
export const STILL_PYTHON = ['/signup'] as const

/**
 * Answered by Python for a POST and by React for a GET.
 *
 * `/login` is a page and an endpoint on one address: the form the reader sees is the app's,
 * and the password it sends goes to the server that has the session. The dev server needs
 * both, which a path-based proxy cannot express on its own.
 */
export const PYTHON_WRITES = ['/login'] as const
